import React, { useMemo, useState, forwardRef, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import Cookies from "universal-cookie";
import { utilities } from "@/_helpers";

import Device from "./Device";
import ScheduleNow from "./appointment/ScheduleNow";
import AppointmentDateSelection from "./appointment/AppointmentDateSelection";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Slide from "@mui/material/Slide";
import { RingLoader } from "@/_components";
import { connect } from "react-redux";
import { getLocation } from "../../_api/appointmentApi";
import { useParams } from "react-router-dom";

const DialogTransition = forwardRef(function DialogTransition(props, ref) {
  return <Slide direction="right" ref={ref} {...props} />;
});

const normalizeLocationDeviceType = (deviceType) => {
  const normalizedDeviceType = String(deviceType || "")
    .trim()
    .toLowerCase();

  if (["1", "iid", "interlock"].includes(normalizedDeviceType))
    return "Interlock";
  if (["5", "pam", "portable"].includes(normalizedDeviceType))
    return "Portable";
  if (["6", "isa", "speed", "speedv"].includes(normalizedDeviceType))
    return "Speed";
  return null;
};

const normalizeLocationDeviceTypes = (deviceTypes) => {
  const values = Array.isArray(deviceTypes)
    ? deviceTypes
    : String(deviceTypes || "").split(/[\s,;|/]+/);

  return values.map(normalizeLocationDeviceType).filter(Boolean);
};

const LOCATION_DEVICE_ORDER = {
  Interlock: 0,
  Portable: 1,
  Speed: 2,
};

const LocationSchedule = ({
  isLoaded,
  handleUpdate,
  STEPS,
  stepSchedule,
  setStepSchedule,
  submitting,
  setGoogleMapData,
  setDeviceLocationType,
  deviceLocationType,
  setActiveStep,
  flagDevice,
  setFlagDevice,
  googleMapDataSession,
  flagClickReset,
  compactScheduleFlow,
  deviceCheck,
  forceCloseScheduleDialog,
  pricing = false,
  forceLocationSelectionEachEntry = false,
  onEnterPricing,
  renderPricingStep,
  canEnterLocationSelection = true,
  onBeforeLocationSelection,
}) => {
  const cookies = new Cookies();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [clickMe, setClickMe] = useState(false);

  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
  const locationFlagData =
    JSON.parse(sessionStorage.getItem("locationFlag")) || null;
  const EnrollNowData = cookies.get("EnrollNow");
  const locationIdFromCookie =
    EnrollNowData?.LocationId || EnrollNowData?.LocationID;
  const deviceFromCookie = cookies.get("EnrollNow")?.DeviceType;
  const deviceFromSession = enrolleeUser?.clientTypeId;
  const isCompactFlow = compactScheduleFlow === true;
  const { storyName } = useParams();
  const useLocationDeviceRules =
    storyName?.toLowerCase() === "location" && !isCompactFlow;
  const deviceFieldType = normalizeLocationDeviceType(deviceFromCookie);
  const enrolleeClientTypeId =
    enrolleeUser?.clientTypeId ??
    enrolleeUser?.ClientTypeId ??
    enrolleeUser?.clientTypeID ??
    enrolleeUser?.ClientTypeID;
  const enrolleeClientDeviceType =
    enrolleeClientTypeId != null
      ? normalizeLocationDeviceType(
          utilities.deviceInfo(enrolleeClientTypeId)?.deviceType,
        )
      : null;
  const enrolleeReturnedDeviceTypes = useMemo(
    () => [
      ...new Set([
        ...(enrolleeClientDeviceType ? [enrolleeClientDeviceType] : []),
        ...normalizeLocationDeviceTypes(
          enrolleeUser?.deviceTypes || enrolleeUser?.DeviceTypes,
        ),
        ...(!enrolleeUser?.deviceSelectionSource
          ? normalizeLocationDeviceTypes(
              enrolleeUser?.deviceType || enrolleeUser?.DeviceType,
            )
          : []),
      ]),
    ],
    [
      enrolleeClientDeviceType,
      enrolleeUser?.DeviceType,
      enrolleeUser?.DeviceTypes,
      enrolleeUser?.deviceSelectionSource,
      enrolleeUser?.deviceType,
      enrolleeUser?.deviceTypes,
    ],
  );
  const enrollmentCodeDeviceTypes = useMemo(
    () => [
      ...new Set(
        (deviceCheck || []).map(normalizeLocationDeviceType).filter(Boolean),
      ),
    ],
    [deviceCheck],
  );
  const hasDeviceField = Boolean(deviceFieldType);
  const hasEnrollmentCodeDevices = enrollmentCodeDeviceTypes.length > 0;
  const confirmedUserDeviceType =
    enrolleeUser?.deviceSelectionConfirmed &&
    enrolleeUser?.deviceSelectionSource === "user"
      ? normalizeLocationDeviceType(
          enrolleeUser?.deviceType || enrolleeUser?.DeviceType,
        )
      : null;
  const hasConflictingEnrolleeDevice =
    hasDeviceField &&
    enrolleeReturnedDeviceTypes.some(
      (enrolleeDeviceType) => enrolleeDeviceType !== deviceFieldType,
    );
  const autoSelectedDeviceType = useLocationDeviceRules
    ? confirmedUserDeviceType
      ? null
      : hasDeviceField &&
          !hasEnrollmentCodeDevices &&
          !hasConflictingEnrolleeDevice
        ? deviceFieldType
        : !hasDeviceField && enrollmentCodeDeviceTypes.length === 1
          ? enrollmentCodeDeviceTypes[0]
          : null
    : null;
  const requiresExplicitDeviceSelection =
    useLocationDeviceRules &&
    !confirmedUserDeviceType &&
    !autoSelectedDeviceType;
  const selectableDeviceTypes = useMemo(() => {
    const deviceTypes = [
      ...new Set(
        [
          deviceFieldType,
          ...enrollmentCodeDeviceTypes,
          ...(hasConflictingEnrolleeDevice ? enrolleeReturnedDeviceTypes : []),
        ].filter(Boolean),
      ),
    ];

    return deviceTypes.sort(
      (firstDevice, secondDevice) =>
        LOCATION_DEVICE_ORDER[firstDevice] -
        LOCATION_DEVICE_ORDER[secondDevice],
    );
  }, [
    deviceFieldType,
    enrollmentCodeDeviceTypes,
    enrolleeReturnedDeviceTypes,
    hasConflictingEnrolleeDevice,
  ]);
  const resolvedDeviceType = useMemo(() => {
    if (useLocationDeviceRules) {
      return (
        confirmedUserDeviceType ||
        autoSelectedDeviceType ||
        normalizeLocationDeviceType(deviceLocationType)
      );
    }

    if (
      !deviceFromCookie &&
      !deviceLocationType &&
      !enrolleeUser?.deviceType &&
      !deviceFromSession
    ) {
      return null;
    }
    return (
      utilities.deviceInfo(
        deviceFromCookie ||
          deviceLocationType ||
          enrolleeUser?.deviceType ||
          deviceFromSession,
      )?.deviceType ?? null
    );
  }, [
    deviceFromCookie,
    deviceLocationType,
    enrolleeUser?.deviceType,
    deviceFromSession,
    confirmedUserDeviceType,
    autoSelectedDeviceType,
    useLocationDeviceRules,
  ]);
  const resolvedDeviceIsPam =
    useLocationDeviceRules && resolvedDeviceType === "Portable";
  const shouldForceDeviceSelection = !isCompactFlow && !resolvedDeviceType;
  const [selectedLocation, setSelectedLocation] = useState(
    locationFlagData?.locationID ? locationFlagData : null,
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(true);
  const [getMap, setGetMap] = useState(false);
  const [jumpedMap, setJumpedMap] = useState(true);
  const [selectedId, setSelectedId] = useState(
    locationFlagData?.locationID || null,
  );
  const [loadingLocation, setLoadingLocation] = useState(false);
  const nextStepAfterLocation = pricing ? STEPS.PRICING : STEPS.DATE_TIME;
  const lastPricingLocationIdRef = useRef(null);
  const automaticallySelectedDeviceRef = useRef(null);

  useEffect(() => {
    if (!useLocationDeviceRules || !autoSelectedDeviceType) return;

    automaticallySelectedDeviceRef.current = autoSelectedDeviceType;
    setDeviceLocationType(autoSelectedDeviceType);
    const currentEnrollee =
      JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
    sessionStorage.setItem(
      "enrolleeUser",
      JSON.stringify({
        ...currentEnrollee,
        deviceType: autoSelectedDeviceType,
        deviceSelectionConfirmed: true,
        deviceSelectionSource: "automatic",
      }),
    );
  }, [autoSelectedDeviceType, setDeviceLocationType, useLocationDeviceRules]);

  useEffect(() => {
    if (
      !requiresExplicitDeviceSelection ||
      !automaticallySelectedDeviceRef.current ||
      normalizeLocationDeviceType(deviceLocationType) !==
        automaticallySelectedDeviceRef.current
    ) {
      return;
    }

    automaticallySelectedDeviceRef.current = null;
    setDeviceLocationType(undefined);
    const currentEnrollee =
      JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
    sessionStorage.setItem(
      "enrolleeUser",
      JSON.stringify({
        ...currentEnrollee,
        deviceType: null,
        deviceSelectionConfirmed: false,
        deviceSelectionSource: null,
      }),
    );
  }, [
    deviceLocationType,
    requiresExplicitDeviceSelection,
    setDeviceLocationType,
  ]);

  const isUsaLocation = async (coords) => {
    if (!isLoaded || !window.google?.maps?.Geocoder) return false;
    const geocoder = new window.google.maps.Geocoder();
    try {
      const response = await geocoder.geocode({
        location: { lat: coords.latitude, lng: coords.longitude },
      });
      const components = response?.results?.[0]?.address_components || [];
      const country = components.find((x) => x.types.includes("country"));
      const short = country?.short_name;
      const long = country?.long_name;
      return short === "US" || short === "USA" || long === "United States";
    } catch {
      return false;
    }
  };
  const handleClose = () => {
    if (clickMe === true) {
      if (!selectedLocation && locationFlagData?.locationID) {
        setSelectedLocation(locationFlagData);
        setSelectedId(locationFlagData.locationID);
      }
      setOpen(false);
      setStepSchedule(nextStepAfterLocation);
      setClickMe(false);
      return;
    }

    if (isCompactFlow) {
      setOpen(false);
      setStepSchedule(STEPS.SCHEDULE_NOW);
      setSelectedLocation(null);
      setSelectedId(null);
      if (typeof setActiveStep === "function") {
        setActiveStep((prev) =>
          typeof prev === "number" ? Math.max(prev - 1, 0) : 0,
        );
      }
      return;
    }

    if (locationIdFromCookie) {
      setOpen(false);
      if (!selectedLocation) {
        setSelectedLocation(locationFlagData);
      }
      setStepSchedule(nextStepAfterLocation);
      return;
    }
    setOpen(false);

    if (navigator.geolocation?.getCurrentPosition) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const isUsa = await isUsaLocation(position.coords);
          setStepSchedule(
            isUsa || isCompactFlow ? STEPS.SCHEDULE_NOW : STEPS.LOCATION_ZIP,
          );
          if (!isUsa && !isCompactFlow) {
            setFlagDevice(true);
          }
        },
        () => {
          setStepSchedule(
            isCompactFlow ? STEPS.SCHEDULE_NOW : STEPS.LOCATION_ZIP,
          );
          if (!isCompactFlow) {
            setFlagDevice(true);
          }
        },
      );
      return;
    }
    setStepSchedule(isCompactFlow ? STEPS.SCHEDULE_NOW : STEPS.LOCATION_ZIP);
    if (!isCompactFlow) {
      setFlagDevice(true);
    }
    setJumpedMap(false);
  };

  useEffect(() => {
    if (selectedLocation?.locationID) {
      sessionStorage.setItem("locationFlag", JSON.stringify(selectedLocation));
      if (!selectedId || selectedId !== selectedLocation.locationID) {
        setSelectedId(selectedLocation.locationID);
      }
    }
  }, [selectedId, selectedLocation]);

  useEffect(() => {
    if (forceCloseScheduleDialog) {
      setOpen(false);
    }
  }, [forceCloseScheduleDialog]);

  useEffect(() => {
    if (stepSchedule !== STEPS.PRICING || !selectedLocation?.locationID) {
      return;
    }
    if (lastPricingLocationIdRef.current === selectedLocation.locationID) {
      return;
    }
    lastPricingLocationIdRef.current = selectedLocation.locationID;
    if (typeof onEnterPricing === "function") {
      onEnterPricing(selectedLocation);
    }
  }, [stepSchedule, selectedLocation, STEPS, onEnterPricing]);

  useEffect(() => {
    if (forceLocationSelectionEachEntry) {
      sessionStorage.removeItem("locationFlag");
      const enrolleeUserSession =
        JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
      sessionStorage.setItem(
        "enrolleeUser",
        JSON.stringify({
          ...enrolleeUserSession,
          locationID: null,
          locationId: null,
          LocationID: null,
        }),
      );
      setSelectedLocation(null);
      setSelectedId(null);
      setStepSchedule(STEPS.SCHEDULE_NOW);
      return;
    }

    const skipInit = sessionStorage.getItem("skipLocationScheduleInit");
    if (skipInit === "1") {
      sessionStorage.removeItem("skipLocationScheduleInit");
      return;
    }

    const jwtToken = cookies.get("lifesaferEnrollment")?.jwtToken;
    const enrollNow = cookies.get("EnrollNow") || {};
    const locationId = enrollNow?.LocationId || enrollNow?.LocationID;

    if (isCompactFlow && jwtToken && deviceCheck?.length > 0 && !locationId) {
      setStepSchedule(STEPS.DEVICE);
    }

    if (shouldForceDeviceSelection) {
      setStepSchedule(STEPS.DEVICE);
      return;
    }

    if (
      isCompactFlow &&
      (stepSchedule === STEPS.DEVICE || stepSchedule === STEPS.LOCATION_ZIP)
    ) {
      setFlagDevice(false);
      setStepSchedule(STEPS.SCHEDULE_NOW);
      return;
    }

    if (!resolvedDeviceType && !flagDevice && locationFlagData) {
      setStepSchedule(isCompactFlow ? STEPS.SCHEDULE_NOW : STEPS.DEVICE);
      return;
    }

    if (!jwtToken) {
      setActiveStep(1);
      return;
    }

    if (!canEnterLocationSelection) {
      if (typeof onBeforeLocationSelection === "function") {
        onBeforeLocationSelection();
      }
      return;
    }

    if (
      !locationId &&
      sessionStorage.getItem("forceLocationZipAfterVehicle") === "true"
    ) {
      setFlagDevice(true);
      setStepSchedule(STEPS.LOCATION_ZIP);
      return;
    }

    if (
      locationId &&
      sessionStorage.getItem("forceLocationZipAfterVehicle") === "true"
    ) {
      sessionStorage.removeItem("forceLocationZipAfterVehicle");
    }

    if (locationFlagData?.locationID && !locationId) {
      setSelectedLocation(locationFlagData);
      setSelectedId(locationFlagData.locationID);
      setStepSchedule(nextStepAfterLocation);
      return;
    }

    if (locationId) {
      setLoadingLocation(true);
      getLocation(locationId)
        .then((location) => {
          setSelectedLocation(location);
          sessionStorage.setItem("locationFlag", JSON.stringify(location));
          setSelectedId(location.locationID || locationId);

          sessionStorage.setItem(
            "googleMapData",
            JSON.stringify({
              homeLat: location.latitude,
              homeLong: location.longitude,
              homeAddr1: location.address1,
              city: location.city,
              state: location.state,
              zip: location.zip,
            }),
          );

          setGoogleMapData({
            homeLat: location.latitude,
            homeLong: location.longitude,
            homeAddr1: location.address1,
            city: location.city,
            state: location.state,
          });

          setStepSchedule(nextStepAfterLocation);
        })
        .catch(() => {
          if (!flagClickReset) {
            setStepSchedule(STEPS.SCHEDULE_NOW);
          }
        })
        .finally(() => {
          setLoadingLocation(false);
        });

      return;
    }

    const hasGeoSupport = Boolean(navigator.geolocation?.getCurrentPosition);
    const hasValidSessionCoords =
      googleMapDataSession?.homeLat != null &&
      googleMapDataSession?.homeLong != null;
    const hasValidUserCoords =
      enrolleeUser?.homeLat != null && enrolleeUser?.homeLong != null;

    if (!hasValidSessionCoords && !hasValidUserCoords && hasGeoSupport) {
      setLoadingLocation(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const isUsa = await isUsaLocation(position.coords);
          setStepSchedule(isUsa ? STEPS.SCHEDULE_NOW : STEPS.LOCATION_ZIP);
          if (!isUsa && !isCompactFlow) {
            setFlagDevice(true);
          }
          setLoadingLocation(false);
        },
        () => {
          setStepSchedule(
            isCompactFlow ? STEPS.SCHEDULE_NOW : STEPS.LOCATION_ZIP,
          );
          if (!isCompactFlow) {
            setFlagDevice(true);
          }
          setLoadingLocation(false);
        },
      );
      return;
    }

    if (!hasValidSessionCoords && !hasValidUserCoords && !hasGeoSupport) {
      setStepSchedule(isCompactFlow ? STEPS.SCHEDULE_NOW : STEPS.LOCATION_ZIP);
      if (!isCompactFlow) {
        setFlagDevice(true);
      }
      return;
    }

    // navigator.geolocation.getCurrentPosition(
    //   () => setStepSchedule(STEPS.SCHEDULE_NOW),
    //   () => {
    //     (!enrolleeUser?.homeLat &&
    //       !enrolleeUser?.homeLong) &&
    //       setStepSchedule(STEPS.LOCATION_ZIP);
    //   },
    // );

    if (
      stepSchedule === STEPS.LOCATION_ZIP &&
      (!enrolleeUser?.homeLat || !enrolleeUser?.homeLong)
    ) {
      if (isCompactFlow) {
        setStepSchedule(STEPS.SCHEDULE_NOW);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const isUsa = await isUsaLocation(position.coords);
          setStepSchedule(isUsa ? STEPS.SCHEDULE_NOW : STEPS.LOCATION_ZIP);
        },
        () => {
          setStepSchedule(STEPS.LOCATION_ZIP);
        },
      );
      return;
    }
  }, [
    resolvedDeviceType,
    deviceCheck,
    shouldForceDeviceSelection,
    forceLocationSelectionEachEntry,
    canEnterLocationSelection,
    onBeforeLocationSelection,
    resolvedDeviceIsPam,
  ]);

  if (
    useLocationDeviceRules &&
    resolvedDeviceType &&
    cookies.get("lifesaferEnrollment")?.jwtToken &&
    !canEnterLocationSelection
  ) {
    return (
      <Grid display="flex" justifyContent="center" alignItems="center">
        <RingLoader />
      </Grid>
    );
  }

  if (
    !isCompactFlow &&
    (stepSchedule === STEPS.DEVICE || stepSchedule === STEPS.LOCATION_ZIP) &&
    (!resolvedDeviceType || flagDevice || shouldForceDeviceSelection)
  ) {
    return (
      <Device
        submitting={submitting}
        isLoaded={isLoaded}
        goToBackStep={null}
        setStepSchedule={setStepSchedule}
        stepSchedule={stepSchedule}
        STEPS={STEPS}
        setGoogleMapData={setGoogleMapData}
        setDeviceLocationType={setDeviceLocationType}
        deviceLocationType={resolvedDeviceType}
        setActiveStep={setActiveStep}
        setOpen={setOpen}
        jumpedMap={jumpedMap}
        availableDeviceTypes={
          useLocationDeviceRules ? selectableDeviceTypes : undefined
        }
        requireExplicitSelection={requiresExplicitDeviceSelection}
      />
    );
  }

  if (stepSchedule === STEPS.SCHEDULE_NOW && !loadingLocation) {
    return (
      <Dialog
        fullWidth
        fullScreen={fullScreen}
        maxWidth="lg"
        open={open}
        onClose={handleClose}
        TransitionComponent={DialogTransition}
        keepMounted={!isCompactFlow}
      >
        <DialogContent sx={{ overflow: isMobile ? "hidden" : "auto" }}>
          {!isLoaded || loadingLocation ? (
            <Grid display="flex" justifyContent="center" alignItems="center">
              <RingLoader />
            </Grid>
          ) : (
            <ScheduleNow
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              setStepSchedule={setStepSchedule}
              STEPS={STEPS}
              isLoaded={isLoaded}
              setGetMap={setGetMap}
              getMap={getMap}
              submitting={submitting}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              loadingLocation={loadingLocation}
              nextStepAfterLocation={nextStepAfterLocation}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  }

  if (stepSchedule === STEPS.PRICING) {
    if (typeof renderPricingStep === "function") {
      return renderPricingStep({
        selectedLocation,
        onBack: () => setStepSchedule(STEPS.SCHEDULE_NOW),
        onContinue: () => setStepSchedule(STEPS.DATE_TIME),
      });
    }
    setStepSchedule(STEPS.DATE_TIME);
    return null;
  }

  if (stepSchedule === STEPS.DATE_TIME) {
    return (
      <Grid
        container
        direction="column"
        alignItems="center"
        sx={{
          paddingTop: storyName?.toLowerCase() === "speed" ? " 24px" : "0px",
        }}
      >
        <AppointmentDateSelection
          selectedLocation={selectedLocation}
          deviceType={resolvedDeviceType}
          preferProvidedDeviceType={useLocationDeviceRules}
          setSelectedLocation={setSelectedLocation}
          setStepSchedule={setStepSchedule}
          STEPS={STEPS}
          isLoaded={isLoaded}
          setGetMap={setGetMap}
          getMap={getMap}
          handleUpdate={handleUpdate}
          submitting={submitting}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          setOpen={setOpen}
          setClickMe={setClickMe}
        />
      </Grid>
    );
  }

  return (
    <>
      <Grid display="flex" justifyContent="center" alignItems="center">
        <RingLoader />
      </Grid>
    </>
  );
};

LocationSchedule.propTypes = {
  isLoaded: PropTypes.bool,
  STEPS: PropTypes.object.isRequired,
  handleUpdate: PropTypes.func,
  stepSchedule: PropTypes.string,
  setStepSchedule: PropTypes.func,
  submitting: PropTypes.bool,
  setActiveStep: PropTypes.func,
  setGoogleMapData: PropTypes.func,
  setDeviceLocationType: PropTypes.func,
  deviceLocationType: PropTypes.string,
  setOpen: PropTypes.func,
  flagDevice: PropTypes.bool,
  setFlagDevice: PropTypes.func,
  googleMapDataSession: PropTypes.object,
  flagClickReset: PropTypes.bool,
  compactScheduleFlow: PropTypes.bool,
  deviceCheck: PropTypes.array,
  forceCloseScheduleDialog: PropTypes.bool,
  pricing: PropTypes.bool,
  forceLocationSelectionEachEntry: PropTypes.bool,
  onEnterPricing: PropTypes.func,
  renderPricingStep: PropTypes.func,
  canEnterLocationSelection: PropTypes.bool,
  onBeforeLocationSelection: PropTypes.func,
};

export default connect(null, {})(LocationSchedule);
